import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { SESSION_REPORT_TYPES } from '@/scheduling/lib/session-report-types';

/**
 * The Technician Worklist's report feed — PROD-2374.
 *
 * `POST /api/Patients/GetDocumentsByAppointments` — the session↔report link the
 * worklist was missing. Before this, a completed row could show its report only
 * for one attached during that page's lifetime: `onSaved` hands back a bare
 * document id, `DayListRow` carries no document reference, and a reload dropped
 * the lot. This is the persisted source that replaces that page-lifetime state.
 *
 * WHAT THE ENDPOINT RETURNS
 * -------------------------
 * A lean per-document projection — `{ appointmentId, patientDocumentId,
 * documentUrl, name, type, documentDate }` — for a batch of appointment ids.
 * Not the full `PatientDocumentModel` the Documents tab reads: this surface
 * needs a name to print and a URL to open, and nothing else.
 *
 * **`documentUrl` IS A PER-REQUEST SAS-SIGNED URL.** The database stores a bare
 * blob path; the server signs it on every read. It therefore has an expiry this
 * client cannot see, so it is fetched, used, and discarded:
 *
 *   - `gcTime: 0` — the cache entry dies with its last observer, so a signed URL
 *     can never be handed to a later mount out of cache;
 *   - `staleTime: 0` — a remount re-signs rather than serving the old signature;
 *   - it is never written to `localStorage`, `sessionStorage`, a store, or a
 *     query key, and never survives a reload. Do not add `keepPreviousData` or
 *     a `staleTime` here to "save a request": the request IS the signature.
 *
 * THE 50-ID CAP
 * -------------
 * The server caps `appointmentIds` at 50. The worklist pages client-side at
 * 10/20/50 (a page is at most `MAX_PAGE_SIZE`, 50), so a page fits the cap; it
 * offered 100 until PROD-3304. Two things keep it closed, and the split matters:
 *
 *   1. the CALLER passes only the ids of the rows currently on screen — a page,
 *      not the whole day (a busy practice's day is unbounded, and a report
 *      nobody can see is a request nobody needed);
 *   2. this hook CHUNKS whatever it is given at {@link SESSION_REPORT_ID_CAP}
 *      and issues one request per chunk, so the 100-row page is two requests of
 *      50 rather than one rejected request of 100.
 *
 * Chunking rather than truncating is the whole point: silently dropping ids 51+
 * would leave the bottom half of a 100-row page showing "no report" for
 * sessions that have one, which is the class of lie this screen is built to
 * avoid. The chunks are awaited together and flattened, so one query key covers
 * the page and a single failure fails the page's reports — never half of them.
 *
 * WHY IT IS FILTERED, AND TO WHAT
 * -------------------------------
 * To {@link SESSION_REPORT_TYPES} — `Radiology` and `Procedure`, both of them
 * (Andrew, 2026-09-19). The fleet is mixed: a DEXA or an ultrasound produces a
 * radiology report, an HBOT or cryotherapy session produces a procedure
 * record, and 57 active devices do not all make the same artefact. The screen
 * shipped filtered to `Procedure` alone, which was a guess, and it made every
 * radiology session look like it had no report.
 *
 * That same constant is what the attach drawer is narrowed to (`allowedTypes`
 * on `NewFilePopup`). The two sides MUST stay in agreement: file under a type
 * the read does not ask for and the document saves fine, the server filters it
 * straight back out, and this screen says "no report" for a session that has
 * one — no error, no log, invisible until a reload. That is why it is one
 * imported constant and not a literal at each end.
 *
 * And it stays FILTERED. Dropping `fileType` is not the easy version of
 * widening it: an unfiltered read was verified on dev to return a **Lab**
 * document for one of these appointments, so it would offer a lab result as
 * the session's report. A document this surface did not produce is not this
 * surface's report.
 *
 * REPORTS ARE SUPPLEMENTARY
 * -------------------------
 * This feed is deliberately NOT part of the worklist's seven-state machine
 * (`use-technician-sessions.ts`). Its own root key, its own query, its own
 * failure. A session list that has loaded must stay on screen, counted and
 * paginated, whether or not its reports ever arrive — the technician's job is
 * running the machine, and a document lookup failing is not a reason to hide
 * the day's work. Nothing here may feed a count, a footer or an empty state.
 */

/** Root segment of this feed's query key. Shared with nothing. */
export const SESSION_REPORTS_QUERY_ROOT = 'session-reports';

/**
 * The server's cap on `appointmentIds` per request.
 *
 * **MUST MATCH the server-side cap.** It is
 * `GetDocumentsByAppointmentsRequest.MaxAppointmentIds` in Scrips.Patient
 * (`Application/Dtos/Patient/GetDocumentsByAppointments.cs`), the DTO behind
 * `POST /api/Patients/GetDocumentsByAppointments`. The server REJECTS an
 * over-cap request rather than truncating it, so if the backend ever lowers
 * that number this constant is what makes a full worklist page start failing
 * instead of quietly working — change them together.
 *
 * Exported so the chunking, the caller and the test that pins the literal all
 * read one number: the test asserts this is 50, so a drift between the two
 * sides has somewhere to surface.
 */
export const SESSION_REPORT_ID_CAP = 50;

/** What a nameless document is called. Better than a blank control. */
const FALLBACK_REPORT_NAME = 'Report';

/** Request body for `POST /api/Patients/GetDocumentsByAppointments`. */
interface GetDocumentsByAppointmentsRequest {
  /** Capped at {@link SESSION_REPORT_ID_CAP} server-side. */
  appointmentIds: string[];
  fileType?: string[];
  isArchived?: boolean;
}

/**
 * One row of the response projection, as the wire may actually ship it —
 * everything optional and nullable, the way every other document contract in
 * this app is typed. The narrowing to {@link SessionReport} happens once, in
 * {@link indexByAppointment}.
 */
interface SessionReportWire {
  appointmentId?: string | null;
  patientDocumentId?: string | null;
  documentUrl?: string | null;
  name?: string | null;
  type?: string | null;
  documentDate?: string | null;
}

/**
 * A report this surface can actually offer: it names a session and it can be
 * opened. Both `appointmentId` and `documentUrl` are REQUIRED here — a document
 * with no URL cannot be opened, and the worklist's rule is that a control never
 * appears unless it works, so such a document is dropped rather than rendered
 * behind a dead eye.
 */
export interface SessionReport {
  appointmentId: string;
  /** The document's own id. Not used to open it — see `documentUrl`. */
  patientDocumentId?: string;
  /** SAS-signed, per request. Never cache, persist or re-use across a reload. */
  documentUrl: string;
  name: string;
  type?: string;
  documentDate?: string;
}

export interface SessionReportsResult {
  /**
   * `appointmentId` → that session's report. Empty while loading, empty on
   * failure, and empty when there are none: the consumer must not read
   * emptiness as "there is no report" — see `isPending`.
   */
  reports: ReadonlyMap<string, SessionReport>;
  /**
   * True only while a request this hook actually MADE is outstanding. `false`
   * when there is nothing to ask about, mirroring the sessions hook's split
   * between "waiting for an answer" and "never going to ask".
   */
  isPending: boolean;
  /** Re-signs and re-reads. Called after an attach, which adds a document. */
  refetch: () => void;
}

/** Shared empty — a fresh `Map` per render would churn every consumer's memo. */
const NO_REPORTS: ReadonlyMap<string, SessionReport> = new Map();

/**
 * Reports for the sessions currently on screen.
 *
 * @param appointmentIds the VISIBLE rows' ids. Pass a page, not a day.
 */
export function useSessionReports(
  appointmentIds: readonly string[],
): SessionReportsResult {
  // De-duplicated, because a duplicate id spends cap budget on an answer we
  // already have. Sorted is deliberately NOT done: the caller's order is the
  // page's order, and re-sorting would remint the key on a re-sort upstream.
  const ids = useMemo(
    () => Array.from(new Set(appointmentIds)),
    [appointmentIds],
  );

  const query = useQuery({
    // React Query hashes keys structurally, so a caller handing over a fresh
    // array of the same ids each render does not remint this key.
    queryKey: [SESSION_REPORTS_QUERY_ROOT, ids],
    enabled: ids.length > 0,
    // DO NOT add `keepPreviousData`, and DO NOT raise `staleTime` or `gcTime`
    // above 0, to save a request — THE REQUEST IS THE SIGNATURE. `documentUrl`
    // is a SAS URI the server mints per read, so anything held in cache is an
    // already-expiring link waiting to be handed to a later mount:
    // `staleTime: 0` makes a remount re-sign instead of serving the old
    // signature, and `gcTime: 0` kills the entry with its last observer so
    // there is nothing left to serve. This is a correctness setting, not an
    // oversight. Full reasoning in the header.
    staleTime: 0,
    gcTime: 0,
    queryFn: async ({ signal }) => {
      const batches = await Promise.all(
        chunk(ids, SESSION_REPORT_ID_CAP).map((batchIds) => {
          const body: GetDocumentsByAppointmentsRequest = {
            appointmentIds: batchIds,
            // The READ half of the write/read agreement — the same constant
            // the attach drawer is narrowed to (`allowedTypes` on
            // `NewFilePopup`). Matched server-side against
            // `PatientDocument.Type` as an exact, case-sensitive string, so a
            // mismatch raises nothing: it returns an empty list and the screen
            // says "no report" for a session that has one.
            //
            // Widen this ONLY by editing `SESSION_REPORT_TYPES`, which moves
            // both sides at once. Never inline a literal here, and never drop
            // the filter — an unfiltered read was verified on dev to return a
            // Lab document for one of these appointments.
            fileType: [...SESSION_REPORT_TYPES],
            // An archived document is one someone removed. Offering it back
            // would undo that decision from a screen that cannot undo anything.
            isArchived: false,
          };
          return post<SessionReportWire[] | null>(
            '/api/Patients/GetDocumentsByAppointments',
            body,
            { signal },
          );
        }),
      );
      return batches.flatMap((batch) => batch ?? []);
    },
  });

  const reports = useMemo(() => indexByAppointment(query.data), [query.data]);

  const refetchQuery = query.refetch;
  const refetch = useCallback(() => {
    void refetchQuery();
  }, [refetchQuery]);

  return {
    reports,
    // `enabled: false` leaves a query `pending` forever, which is "we are never
    // going to ask" — not "we are waiting". The consumer must be able to tell
    // those apart, exactly as it must on the sessions feed.
    isPending: ids.length > 0 && query.isPending,
    refetch,
  };
}

/** `[1,2,3,4,5]`, size 2 → `[[1,2],[3,4],[5]]`. Empty in, empty out. */
function chunk(ids: readonly string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

/**
 * The projection, indexed by session.
 *
 * TWO documents can name the same appointment — a report re-filed after a bad
 * scan, say — and this surface has room for exactly one. The most recent
 * `documentDate` wins; see {@link isNewerThan} for what happens when a date
 * cannot be read.
 *
 * ON AN EXACT TIE, THE SERVER PICKS THE WINNER, NOT THIS HOOK. Reproduced
 * live: two `Procedure` documents against one appointment with an IDENTICAL
 * `documentDate` — which is routine, because the drawer files a date, not an
 * instant, so two reports attached on the same day tie. {@link isNewerThan}
 * is a strict `>`, so a tie returns false, the incumbent is kept, and the
 * FIRST row of the response therefore wins. Which row that is comes from the
 * endpoint's own `ORDER BY pd.UpdatedOn DESC`, so what is observed is
 * "most-recently-updated wins" — which IS the right answer.
 *
 * But it is the SERVER'S ordering that makes it right, not anything in this
 * file. Nothing here would notice if that `ORDER BY` changed: the tie would
 * quietly start resolving the other way and a superseded report would take
 * the row. To guarantee it client-side the projection would have to start
 * shipping a field this hook can actually compare — `updatedOn` — and the
 * tie-break moved onto it. Do NOT instead relax the comparison to `>=`: that
 * makes the LAST row win, which under the server's ordering is the OLDEST.
 */
function indexByAppointment(
  docs: readonly SessionReportWire[] | undefined,
): ReadonlyMap<string, SessionReport> {
  if (!docs || docs.length === 0) return NO_REPORTS;

  const out = new Map<string, SessionReport>();
  for (const doc of docs) {
    const appointmentId = doc?.appointmentId;
    const documentUrl = doc?.documentUrl;
    // DROPPED, deliberately. A row that names no session cannot be placed, and
    // one with no URL cannot be opened — rendering it would put an eye on the
    // screen that does nothing, which is the defect this ticket is closing.
    if (!appointmentId || !documentUrl) continue;

    const candidate: SessionReport = {
      appointmentId,
      patientDocumentId: doc.patientDocumentId ?? undefined,
      documentUrl,
      // `.trim() ||` not `??`: a whitespace-only name is a blank control.
      name: doc.name?.trim() || FALLBACK_REPORT_NAME,
      type: doc.type ?? undefined,
      documentDate: doc.documentDate ?? undefined,
    };

    const held = out.get(appointmentId);
    if (!held || isNewerThan(candidate.documentDate, held.documentDate)) {
      out.set(appointmentId, candidate);
    }
  }
  return out.size === 0 ? NO_REPORTS : out;
}

/**
 * Is `a` a later instant than `b`?
 *
 * An unreadable or missing date NEVER displaces a document already held — the
 * alternative is letting a malformed timestamp win a tie-break it cannot
 * justify. An unreadable incumbent, on the other hand, yields to any readable
 * challenger: something dated beats nothing dated.
 */
function isNewerThan(a: string | undefined, b: string | undefined): boolean {
  const at = a === undefined ? NaN : new Date(a).getTime();
  const bt = b === undefined ? NaN : new Date(b).getTime();
  if (Number.isNaN(at)) return false;
  if (Number.isNaN(bt)) return true;
  return at > bt;
}
